// fileDownloads.js
// Download of uploaded files and what cleaning made of them (Spec 2 section 6.3).
//
// A file's download holds the file as stored, the file exactly as uploaded when it
// was converted from an older Office format (`pre_converted_prefix`), the whole
// workbook a split sheet was cut from (`pre_split_original_prefix`), and the
// assertion workbook of each of its ingest units: the rows each table will say to
// the table, after cleaning. The file document's ledger carries no storage paths,
// so the units are read with one `file_id` query when the download is asked for;
// no list or card subscribes to them.
//
// A storage path read off a document is followed only inside its owner's folder
// in this company's storage; one that is not is named as a member that could not
// be downloaded, never fetched.

import { collection, getDocs, query, where } from 'firebase/firestore';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { db } from './firebase';
import { downloadGcsFileByPrefix, fetchGcsBlobByPrefix } from './api_utils';
import { companyScope, FILES, preConvertedName, PRE_CONVERTED, tableScope } from '../utils/blobPaths';
import { makeZipNameAllocator } from '../utils/zipNames';

// Storage fetches and unit queries in flight at once.
const PARALLEL_WORK = 8;

// The storage families named here, as functions/common/blob_paths.py names them.
const PRE_SPLIT_ORIGINAL = 'pre_split_original';
const ASSERTIONS = 'assertions';

function hasNoParentStep(path) {
  return !path.split('/').includes('..');
}

// A file's own upload, and a unit's assertion, live in the table's storage.
function inTableFolder(path, companyId, tableId, family, ownerId) {
  return typeof path === 'string'
    && path.startsWith(`${tableScope(companyId, tableId)}/${family}/${ownerId}/`)
    && hasNoParentStep(path);
}

// A pre-conversion or pre-split original follows its file through the workspace's
// staging under the file's own id, so it may sit in the company's storage or the
// table's, always in the file's own folder of its family.
function inFileFolderOfCompany(path, companyId, family, fileId) {
  return typeof path === 'string'
    && path.startsWith(`${companyScope(companyId)}/`)
    && path.includes(`/${family}/${fileId}/`)
    && hasNoParentStep(path);
}

// Runs `work` over every item, at most PARALLEL_WORK at a time, and returns the
// results in the items' order.
async function mapWithLimit(items, work) {
  const results = new Array(items.length);
  let nextIndex = 0;
  const worker = async () => {
    while (nextIndex < items.length) {
      const index = nextIndex;
      nextIndex += 1;
      results[index] = await work(items[index]);
    }
  };
  const workers = Math.min(PARALLEL_WORK, items.length);
  await Promise.all(Array.from({ length: workers }, () => worker()));
  return results;
}

// A file's ingest units that have an assertion workbook, in the file's own order:
// sheets as in the workbook, tables as in each sheet. One query on `file_id`.
async function unitsWithAssertions(companyId, tableId, fileId) {
  const unitsQuery = query(
    collection(db, `users/${companyId}/tables/${tableId}/units`),
    where('file_id', '==', fileId),
  );
  const snapshot = await getDocs(unitsQuery);
  return snapshot.docs
    .map((unitDoc) => ({ id: unitDoc.id, ...unitDoc.data() }))
    .filter((unit) => unit.disposition === 'ingest' && unit.assertion)
    .sort((left, right) => left.order - right.order);
}

// One name allocator per zip folder: two files of one download may share a name
// (blobs are id-keyed precisely so they can), and a zip entry with a name already
// used would silently replace the first.
function newZipNameAllocators() {
  return {
    raw: makeZipNameAllocator(),
    uploaded_original: makeZipNameAllocator(),
    split_original: makeZipNameAllocator(),
    cleaned: makeZipNameAllocator(),
  };
}

// The download's members for one file and its units: [{ folder, prefix,
// filename }], with `prefix` null for a pointer outside its owner's folder.
// `allocators` is shared across the files of one zip.
function downloadEntries(companyId, tableId, file, units, allocators) {
  const entries = [{
    folder: 'raw',
    prefix: inTableFolder(file.prefix, companyId, tableId, FILES, file.id) ? file.prefix : null,
    filename: allocators.raw(file.name),
  }];
  if (file.pre_converted_prefix) {
    const owned = inFileFolderOfCompany(file.pre_converted_prefix, companyId, PRE_CONVERTED, file.id);
    entries.push({
      folder: 'uploaded_original',
      prefix: owned ? file.pre_converted_prefix : null,
      filename: allocators.uploaded_original(preConvertedName(file.name, file.pre_converted_prefix)),
    });
  }
  if (file.pre_split_original_prefix) {
    const owned = inFileFolderOfCompany(file.pre_split_original_prefix, companyId, PRE_SPLIT_ORIGINAL, file.id);
    entries.push({
      folder: 'split_original',
      prefix: owned ? file.pre_split_original_prefix : null,
      filename: allocators.split_original(preConvertedName(file.name, file.pre_split_original_prefix)),
    });
  }
  units.forEach((unit) => {
    const owned = inTableFolder(unit.assertion.path, companyId, tableId, ASSERTIONS, unit.id);
    entries.push({
      folder: 'cleaned',
      prefix: owned ? unit.assertion.path : null,
      filename: allocators.cleaned(`${unit.name}.xlsx`),
    });
  });
  return entries;
}

// Fetches every entry. A member that failed every retry, or whose pointer is not
// followed, is named in `failed`, never dropped quietly.
async function fetchEntries(entries) {
  const blobs = await mapWithLimit(entries, async (entry) => {
    if (!entry.prefix) return null;
    try {
      return await fetchGcsBlobByPrefix(entry.prefix);
    } catch (error) {
      console.error('Failed to download a zip member:', entry, error);
      return null;
    }
  });
  const fetched = [];
  const failed = [];
  entries.forEach((entry, index) => {
    if (blobs[index]) fetched.push({ ...entry, blob: blobs[index] });
    else failed.push(entry.filename);
  });
  return { fetched, failed };
}

// Zips the entries under their folders and saves the archive. Returns
// { savedCount, failed }; nothing is saved when no member could be fetched.
async function saveEntriesAsZip(entries, zipName) {
  const { fetched, failed } = await fetchEntries(entries);
  if (fetched.length === 0) return { savedCount: 0, failed };
  const zip = new JSZip();
  fetched.forEach((item) => {
    zip.folder(item.folder).file(item.filename, item.blob);
  });
  const zipBlob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE', compressionOptions: { level: 6 } });
  saveAs(zipBlob, zipName);
  return { savedCount: fetched.length, failed };
}

// One file's download: the file alone when nothing else belongs with it, else a
// zip named after it. Returns { savedCount, failed }.
export async function downloadFile(companyId, tableId, file) {
  const units = await unitsWithAssertions(companyId, tableId, file.id);
  const entries = downloadEntries(companyId, tableId, file, units, newZipNameAllocators());
  if (entries.length === 1) {
    if (!entries[0].prefix) return { savedCount: 0, failed: [file.name] };
    await downloadGcsFileByPrefix(entries[0].prefix, file.name);
    return { savedCount: 1, failed: [] };
  }
  return saveEntriesAsZip(entries, `${file.name}.zip`);
}

// Several files' downloads in one zip, the files in the order given. Each file's
// units are read with its own query; the names are allocated in the files' order
// once every query has answered, so the same selection always zips the same way.
// Returns { savedCount, failed }.
export async function downloadFiles(companyId, tableId, files, zipName) {
  const unitsByFile = await mapWithLimit(files, (file) => unitsWithAssertions(companyId, tableId, file.id));
  const allocators = newZipNameAllocators();
  const entries = files.flatMap((file, index) => downloadEntries(companyId, tableId, file, unitsByFile[index], allocators));
  return saveEntriesAsZip(entries, zipName);
}
